/**
 * MUTAGIM.SHOP — Customer Loyalty Page (Phase 19)
 * Page fidélité dans le compte client
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { toast } from 'sonner';
import {
  ArrowLeft, Star, Gift, Trophy, Clock, Sparkles,
  ChevronRight, Crown, Medal, Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LoyaltyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [balance, setBalance] = useState({
    points: 0,
    lifetime_points: 0,
    tier: 'bronze',
    next_reward: null,
    history: []
  });
  const [rewards, setRewards] = useState([]);
  const [isRedeeming, setIsRedeeming] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData(token);
  }, [navigate]);

  const fetchData = async (authToken) => {
    try {
      const [balanceRes, rewardsRes] = await Promise.all([
        fetch(`${API_URL}/api/engagement/loyalty/balance`, {
          headers: { Authorization: `Bearer ${authToken}` }
        }),
        fetch(`${API_URL}/api/engagement/loyalty/rewards`)
      ]);
      
      if (balanceRes.ok) {
        setBalance(await balanceRes.json());
      }
      if (rewardsRes.ok) {
        const data = await rewardsRes.json();
        setRewards(data.rewards || []);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRedeem = async (rewardId, pointsRequired) => {
    if (balance.points < pointsRequired) {
      toast.error('Points insuffisants');
      return;
    }
    
    setIsRedeeming(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/engagement/loyalty/redeem/${rewardId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Récompense réclamée ! ${data.promo_code ? `Code: ${data.promo_code}` : ''}`);
        fetchData(token);
      } else {
        toast.error('Erreur lors de l\'échange');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setIsRedeeming(false);
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'gold': return <Crown className="h-6 w-6 text-yellow-500" />;
      case 'silver': return <Medal className="h-6 w-6 text-gray-400" />;
      case 'platinum': return <Award className="h-6 w-6 text-purple-500" />;
      default: return <Star className="h-6 w-6 text-orange-500" />;
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'gold': return 'Gold';
      case 'silver': return 'Silver';
      case 'platinum': return 'Platinum';
      default: return 'Bronze';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-mutagim-dark flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-mutagim-red border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark" data-testid="loyalty-page">
      <Header />
      
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/account')}>
            <ArrowLeft className="h-5 w-5 text-white" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Programme Fidélité</h1>
            <p className="text-gray-400">Gagnez des points et débloquez des récompenses</p>
          </div>
        </div>

        {/* Points Card */}
        <Card className="bg-gradient-to-br from-mutagim-red to-red-800 text-white border-0 mb-6">
          <CardContent className="py-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {getTierIcon(balance.tier)}
                <Badge className="bg-white/20 text-white border-0">
                  {getTierLabel(balance.tier)}
                </Badge>
              </div>
              <Sparkles className="h-8 w-8 opacity-50" />
            </div>
            
            <div className="text-center mb-6">
              <p className="text-lg opacity-80 mb-1">Vos points</p>
              <p className="text-5xl font-bold">{balance.points}</p>
              <p className="text-sm opacity-60 mt-2">
                {balance.lifetime_points} points cumulés
              </p>
            </div>
            
            {balance.next_reward && (
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span>Prochain palier</span>
                  <span>{balance.next_reward.points_required} points</span>
                </div>
                <Progress 
                  value={(balance.points / balance.next_reward.points_required) * 100}
                  className="h-2 bg-white/20"
                />
                <p className="text-sm mt-2 opacity-80">
                  Plus que {balance.next_reward.points_needed} points pour : {balance.next_reward.description?.fr || 'Récompense'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* How to earn */}
        <Card className="bg-mutagim-card border-gray-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Comment gagner des points
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-800 rounded-lg">
                <p className="text-mutagim-red font-bold text-xl">1pt / €</p>
                <p className="text-gray-400 text-sm">Pour chaque euro dépensé</p>
              </div>
              <div className="p-4 bg-gray-800 rounded-lg">
                <p className="text-mutagim-red font-bold text-xl">+50 pts</p>
                <p className="text-gray-400 text-sm">Premier achat</p>
              </div>
              <div className="p-4 bg-gray-800 rounded-lg">
                <p className="text-mutagim-red font-bold text-xl">+25 pts</p>
                <p className="text-gray-400 text-sm">Anniversaire</p>
              </div>
              <div className="p-4 bg-gray-800 rounded-lg">
                <p className="text-mutagim-red font-bold text-xl">+10 pts</p>
                <p className="text-gray-400 text-sm">Avis laissé</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rewards */}
        <Card className="bg-mutagim-card border-gray-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Gift className="h-5 w-5 text-mutagim-red" />
              Récompenses disponibles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rewards.map((reward, idx) => {
                const canRedeem = balance.points >= reward.points_required;
                
                return (
                  <div 
                    key={reward.id || idx}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      canRedeem ? 'bg-gray-800 border-mutagim-red' : 'bg-gray-900 border-gray-700'
                    }`}
                  >
                    <div>
                      <p className={`font-medium ${canRedeem ? 'text-white' : 'text-gray-500'}`}>
                        {reward.description?.fr || 'Récompense'}
                      </p>
                      <p className={`text-sm ${canRedeem ? 'text-mutagim-red' : 'text-gray-600'}`}>
                        {reward.points_required} points
                      </p>
                    </div>
                    <Button
                      onClick={() => handleRedeem(reward.id, reward.points_required)}
                      disabled={!canRedeem || isRedeeming}
                      className={canRedeem ? 'bg-mutagim-red hover:bg-mutagim-red/90' : ''}
                      data-testid={`redeem-${idx}`}
                    >
                      Échanger
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* History */}
        {balance.history.length > 0 && (
          <Card className="bg-mutagim-card border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Historique
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {balance.history.map((item, idx) => (
                  <div 
                    key={item.id || idx}
                    className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0"
                  >
                    <div>
                      <p className="text-white text-sm">{item.description}</p>
                      <p className="text-gray-500 text-xs">
                        {item.created_at ? new Date(item.created_at).toLocaleDateString('fr-FR') : ''}
                      </p>
                    </div>
                    <span className={`font-bold ${item.points > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {item.points > 0 ? '+' : ''}{item.points}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default LoyaltyPage;
